import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';
import SettingsIcon from '../../../images/vector/settings.svg';
import { ApolloContext } from '../../../ApolloContextProvider';
import { SPACE } from '../../../constants/keyboardCodes';
import {
    CAPTIONS,
    ALYX_ANIMATION,
    TEXT_SIZE,
    DEFAULT_FONT_SIZE_SCALE,
    MEDIUM_FONT_SIZE_SCALE,
    MAXIMUM_FONT_SIZE_SCALE,
} from '../../../constants/accessibility';
import Tooltip from './Tooltip';
import selectors from '../../../rootSelectors';
import { LANGUAGES } from '../../localization/consts';
import { dataDogAddWarning } from '../../logging/datadog';

const AccessibilitySettingsDropdown = () => {
    const {
        setAccessibilityConfigValues,
        isCaptionsEnabled,
        isAnimationVisualReduced,
        fontSizeScaleValue,
    } = useContext(ApolloContext);
    const [accessibilityFontSize, setAccessibilityFontSize] = useState(fontSizeScaleValue);
    const [isDropdownVisible, showDropdown] = useState(false);
    // Tracks which control is currently in flight. Non-null means a request is pending,
    // which disables all controls. The specific value drives the spinner on the active control only.
    const [loadingControl, setLoadingControl] = useState(null);
    const isLoading = loadingControl !== null;
    const menuRef = useRef();
    const settingsIconRef = useRef();
    const language = useSelector(selectors.getCurrentLanguage);
    const shouldEnableZoom = language === LANGUAGES.ENGLISH || language === LANGUAGES.SPANISH;

    useEffect(() => {
        setAccessibilityFontSize(fontSizeScaleValue);
    }, [fontSizeScaleValue]);

    const hideDropdown = useCallback((e = null) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        showDropdown(false);
    }, []);

    const handleClickOutside = useCallback(
        event => {
            if (
                isDropdownVisible &&
                ((menuRef.current && !menuRef.current.contains(event.target)) ||
                    (settingsIconRef.current && settingsIconRef.current.contains(event.target)))
            ) {
                hideDropdown(event);
            }
        },
        [isDropdownVisible, hideDropdown],
    );

    useEffect(() => {
        if (isDropdownVisible) {
            document.addEventListener('click', handleClickOutside, true);
            return () => {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }
        return () => {};
    }, [isDropdownVisible, handleClickOutside]);

    const resetZoomValue = useCallback(() => {
        if (!shouldEnableZoom) {
            if (fontSizeScaleValue !== DEFAULT_FONT_SIZE_SCALE) {
                setAccessibilityConfigValues({ fontSizeScaleValue: DEFAULT_FONT_SIZE_SCALE });
            }
        }
    }, [shouldEnableZoom, fontSizeScaleValue, setAccessibilityConfigValues]);

    useEffect(() => {
        resetZoomValue();
    }, [shouldEnableZoom, fontSizeScaleValue, resetZoomValue]);

    const onDropdownClick = useCallback(
        e => {
            e.preventDefault();
            if (isDropdownVisible) {
                hideDropdown(e);
            } else {
                showDropdown(true);
            }
        },
        [isDropdownVisible, hideDropdown, resetZoomValue],
    );

    const onDropdownKeyDown = useCallback(
        e => {
            if (e.keyCode !== SPACE) {
                return;
            }
            e.preventDefault();
            if (isDropdownVisible) {
                hideDropdown(e);
            } else {
                showDropdown(true);
            }
        },
        [isDropdownVisible, hideDropdown],
    );

    const toggleCaptionsVisibility = async val => {
        setLoadingControl(CAPTIONS);
        try {
            await setAccessibilityConfigValues({ isCaptionsEnabled: val });
        } catch (error) {
            dataDogAddWarning('Failed to save captions setting', { error: error?.message });
        } finally {
            setLoadingControl(null);
        }
    };

    const toggleAlyxAnimation = async val => {
        setLoadingControl(ALYX_ANIMATION);
        try {
            await setAccessibilityConfigValues({ isAnimationVisualReduced: !val });
        } catch (error) {
            dataDogAddWarning('Failed to save animation setting', { error: error?.message });
        } finally {
            setLoadingControl(null);
        }
    };

    const changeTextSize = async fontSize => {
        setAccessibilityFontSize(fontSize);
        setLoadingControl(TEXT_SIZE);
        try {
            await setAccessibilityConfigValues({ fontSizeScaleValue: fontSize });
        } catch (error) {
            dataDogAddWarning('Failed to save text size setting', { error: error?.message });
        } finally {
            setLoadingControl(null);
        }
    };

    const textSizeOptions = [
        { scale: DEFAULT_FONT_SIZE_SCALE, className: 'btn-s', ariaLabel: 'Small text size' },
        { scale: MEDIUM_FONT_SIZE_SCALE, className: 'btn-m', ariaLabel: 'Medium text size' },
        { scale: MAXIMUM_FONT_SIZE_SCALE, className: 'btn-l', ariaLabel: 'Large text size' },
    ];

    return (
        <div className="settings">
            <button
                className="settings-icon-button"
                aria-label="settings"
                id="settings-button"
                aria-expanded={isDropdownVisible}
                aria-haspopup="menu"
                ref={settingsIconRef}
                onClick={onDropdownClick}
                onKeyDown={onDropdownKeyDown}
            >
                <SettingsIcon className="settings-icon" />
            </button>
            <div className="menu-container">
                <div
                    ref={menuRef}
                    className={`dropdown-menu ${isDropdownVisible ? 'active' : 'inactive'}`}
                    role="menu"
                    aria-expanded={isDropdownVisible}
                    aria-labelledby="settings-button"
                >
                    <ul>
                        <li>
                            Text Size
                            <div className="text-size-controls">
                                {textSizeOptions.map(({ scale, className, ariaLabel }) => (
                                    <div key={className}>
                                        <button
                                            className={`${className} ${
                                                accessibilityFontSize === scale ? 'active' : ''
                                            }`}
                                            aria-label={ariaLabel}
                                            onClick={() => changeTextSize(scale)}
                                            disabled={isLoading}
                                        >
                                            T
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </li>
                        <li>
                            Closed Captioning
                            <ToggleSwitch
                                id="captions"
                                isLoading={loadingControl === CAPTIONS}
                                isDisabled={isLoading}
                                checked={isCaptionsEnabled}
                                onChange={val => toggleCaptionsVisibility(val)}
                            />
                        </li>
                        <li>
                            <Tooltip
                                text={
                                    isAnimationVisualReduced
                                        ? 'Enable animation'
                                        : 'Disable animation'
                                }
                            >
                                Animation
                                <ToggleSwitch
                                    id="alyxAnimation"
                                    isLoading={loadingControl === ALYX_ANIMATION}
                                    isDisabled={isLoading}
                                    checked={!isAnimationVisualReduced}
                                    onChange={val => toggleAlyxAnimation(val)}
                                />
                            </Tooltip>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};
export default AccessibilitySettingsDropdown;
